/* Reveal-on-scroll for the contact rows.

   Wrapped in a function so nothing here is a global binding: two of these
   sections on one page render the file twice, and a top-level class is a
   lexical binding like `let` — the second execution would throw before
   reaching the guard at the bottom. */
(function () {
  class ContactRows extends HTMLElement {
    connectedCallback() {
      /* Nothing below may hide a row until this script is certainly running
         and has certainly found them. The CSS hides on `.ctr--reveal-ready`,
         which only this method adds, so every early return here leaves the
         rows exactly as the page rendered them: visible.

         The inverse — hiding in the stylesheet and relying on this file to
         reveal — is what made the section disappear: a stale copy of this
         script looking for the old attribute found no rows, returned, and left
         every row at opacity 0 with nothing able to bring it back. */
      this.rows = Array.from(this.querySelectorAll('[data-ctr-row]'));
      if (this.rows.length === 0) return;

      // The section was set to arrive at rest.
      if (!this.classList.contains('ctr--reveal')) return;

      /* No observer, or the visitor asked for less motion. Both mean no
         animation, and no animation means no reason to hide anything first. */
      if (
        !('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }

      // Past every guard: safe to let the CSS hide the rows.
      this.classList.add('ctr--reveal-ready');

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        /* A negative bottom margin holds the reveal until the row is properly
           in the viewport rather than a pixel into it, which is what stops the
           whole list animating at once on a short page. */
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1,
      });

      this.rows.forEach((row, i) => {
        /* Staggered by position so the list reads downwards instead of
           flashing as one block. Capped, or a long list leaves the last row
           waiting noticeably after the reader has reached it. */
        row.style.transitionDelay = `${Math.min(i, 6) * 70}ms`;
        this.observer.observe(row);
      });

      /* A backstop. If anything prevents the observer from ever firing — a row
         that is display:none when observed, a layout that settles late — the
         rows must not stay hidden. After a beat, show whatever is still
         waiting; rows already revealed are unaffected. */
      this.failSafe = setTimeout(() => this.revealAll(), 3000);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.failSafe);
    }

    revealAll() {
      this.rows.forEach((row) => row.classList.add('is-visible'));
      this.observer?.disconnect();
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        // One-way: a row that has arrived stays arrived.
        this.observer.unobserve(entry.target);
      });
    }
  }

  if (!customElements.get('contact-rows')) {
    customElements.define('contact-rows', ContactRows);
  }
})();
